import React from 'react';
import { StackShim as Stack, StackItemShim as StackItem } from '@fluentui/react-migration-v8-v9';
import { TrophyFilled, CheckmarkCircleFilled, CircleFilled } from '@fluentui/react-icons';
import { makeStyles, Tooltip, tokens } from '@fluentui/react-components';
import { MilestoneStatus } from '../Interfaces';
import { MessagingColors } from '../../../../app/common/styles/CommonStyleObjects';

const STATUS_ICON_SIZE = '29px';
const TROPHY_ICON_SIZE = '18px';

const useStyles = makeStyles({
  root: {
    marginTop: '15px',
    background: tokens.colorNeutralBackground2,
  },
  goalIconWrapper: {
    position: 'relative',
    width: STATUS_ICON_SIZE,
    height: STATUS_ICON_SIZE,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  trophyIconBlue: {
    border: '6px solid transparent',
    color: tokens.colorBrandBackground,
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
  trophyIconWhite: {
    border: '6px solid transparent',
    color: tokens.colorNeutralForegroundInverted,
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
  completeIcon: {
    border: '2px solid transparent',
    color: MessagingColors.successIcon,
  },
  readyIcon: {
    border: '2px solid transparent',
    color: tokens.colorBrandBackground,
  },
  notStartedIcon: {
    color: tokens.colorNeutralForegroundDisabled,
    borderRadius: tokens.borderRadiusCircular,
  },
  dividerContainer: {
    display: 'block',
    position: 'relative',
  },
  baseDivider: {
    height: '100%',
    width: tokens.strokeWidthThick,
    position: 'absolute',
    left: 'calc(50% - 1px)',
    top: '0',
    backgroundColor: tokens.colorNeutralStroke1,
  },
  progressDivider: {
    position: 'absolute',
    top: '0',
    left: 'calc(50% - 1px)',
    width: tokens.strokeWidthThick,
    backgroundColor: MessagingColors.successIcon,
    transition: 'all 350ms cubic-bezier(0, 0, 0.58, 1)',
  },
  progressComplete: {
    height: '100%',
  },
  progressIncomplete: {
    height: '0%',
  },
});

interface ITimelineNavItemProps {
  status: MilestoneStatus;
  isLast?: boolean;
  isGoalMilestone: boolean;
}

const TimelineNavItem = ({
  status,
  isLast,
  isGoalMilestone,
}: ITimelineNavItemProps): JSX.Element => {
  const styles = useStyles();
  const progressClass =
    status === MilestoneStatus.complete ? styles.progressComplete : styles.progressIncomplete;
  return (
    <Stack verticalFill className={styles.root}>
      <StackItem grow={0}>
        {isGoalMilestone && status === MilestoneStatus.complete && (
          <Tooltip content="Goal Milestone" relationship="label">
            <div className={styles.goalIconWrapper}>
              <CircleFilled
                aria-label="Completed"
                className={styles.completeIcon}
                style={{ fontSize: STATUS_ICON_SIZE }}
              />
              <TrophyFilled
                aria-label="Goal Milestone"
                style={{ fontSize: TROPHY_ICON_SIZE }}
                className={styles.trophyIconWhite}
              />
            </div>
          </Tooltip>
        )}
        {isGoalMilestone && status === MilestoneStatus.ready && (
          <Tooltip content="Goal Milestone" relationship="label">
            <div className={styles.goalIconWrapper}>
              <CircleFilled
                aria-label="Ready"
                className={styles.readyIcon}
                style={{ fontSize: STATUS_ICON_SIZE }}
              />
              <TrophyFilled
                aria-label="Goal Milestone"
                style={{ fontSize: TROPHY_ICON_SIZE }}
                className={styles.trophyIconBlue}
              />
            </div>
          </Tooltip>
        )}
        {isGoalMilestone && status === MilestoneStatus.notStarted && (
          <Tooltip content="Goal Milestone" relationship="label">
            <div className={styles.goalIconWrapper}>
              <CircleFilled
                aria-label="Not Started"
                className={styles.notStartedIcon}
                style={{ fontSize: STATUS_ICON_SIZE }}
              />
              <TrophyFilled
                aria-label="Goal Milestone"
                style={{ fontSize: TROPHY_ICON_SIZE }}
                className={styles.trophyIconBlue}
              />
            </div>
          </Tooltip>
        )}
        {status === MilestoneStatus.complete && !isGoalMilestone && (
          <Tooltip content="Completed" relationship="label">
            <CheckmarkCircleFilled
              aria-label="Completed"
              className={styles.completeIcon}
              style={{ fontSize: STATUS_ICON_SIZE }}
            />
          </Tooltip>
        )}
        {status === MilestoneStatus.ready && !isGoalMilestone && (
          <Tooltip content="Ready" relationship="label">
            <CircleFilled
              aria-label="Ready"
              className={styles.readyIcon}
              style={{ fontSize: STATUS_ICON_SIZE }}
            />
          </Tooltip>
        )}
        {status === MilestoneStatus.notStarted && !isGoalMilestone && (
          <Tooltip content="Not Started" relationship="label">
            <CircleFilled
              aria-label="Not Started"
              className={styles.notStartedIcon}
              style={{ fontSize: STATUS_ICON_SIZE }}
            />
          </Tooltip>
        )}
      </StackItem>
      {!isLast && (
        <StackItem grow={1} className={styles.dividerContainer}>
          <div className={styles.baseDivider} />
          <div className={`${styles.progressDivider} ${progressClass}`} />
        </StackItem>
      )}
    </Stack>
  );
};

export default TimelineNavItem;
